import {
  getGetApiCertificationProgramListQueryOptions,
  type CertificationProgramListItemDTO,
} from '@smartsash/api-client';
import type { TableFilterFieldOverrides, TableFilterOption } from '@smartsash/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CERTIFICATIONS_GROUP_FIELD,
  CERTIFICATIONS_LAB_FIELD,
  certificationColumnText,
  certificationsQueryParams,
  type CertificationColumnId,
  type CertificationsSearch,
} from './certificationsModel';

import { useGroupNames, useInstitutionNames, useLabNames } from '@/hooks/useLookupNames';
import { DataScreenFilter, filterColumnsFromText } from '@/ui/shared/TableFilter';

/** A lookup map, as the options a filter field offers, ordered by label. */
function toOptions(names: Map<string, string>): TableFilterOption[] {
  return [...names]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The filter builder for the Certifications list (HS-1162).
 *
 * The rows come from the **same** query key the screen renders — `certificationsQueryParams` builds
 * it identically here and in `certificationsScreen` — so this is a cache read rather than a second
 * request.
 *
 * Four fields reach a real parameter, and only those four are offered. `validityDays`, `videos` and
 * `questions` are excluded: the endpoint has no parameter for any of them, and a `contains` row on
 * a numeric column would instead fold into the cross-field `search`, which matches `labName` and
 * `institutionName` — so filtering "videos contains 3" would quietly search laboratory names for a
 * "3". A field that cannot narrow the result set must not appear in the builder.
 *
 * Laboratory and Institute display names but carry ids, because `labId` and `institutionId` are
 * what the endpoint narrows on, and their vocabularies come from the lookup queries rather than
 * from the loaded page — pagination is server-driven, so ten rows would offer only the handful of
 * laboratories among them. Group is not a column at all and is appended as an extra field.
 *
 * Status is `enum-one`, not the checkbox list the other three use: its query parameter is a scalar
 * enum, so exactly one value can ever be in effect. Offering a multi-select would build a query the
 * endpoint rejects outright.
 */
export function CertificationsFilter({ search }: { search: CertificationsSearch }) {
  const { t } = useTranslation();
  const { data } = useQuery(
    getGetApiCertificationProgramListQueryOptions(certificationsQueryParams(search)),
  );
  const institutionNames = useInstitutionNames();
  const labNames = useLabNames();
  const groupNames = useGroupNames();

  const columns = useMemo(
    () =>
      filterColumnsFromText<CertificationProgramListItemDTO>(
        {
          laboratory: certificationColumnText.laboratory,
          institute: certificationColumnText.institute,
          validityDays: certificationColumnText.validityDays,
          videos: certificationColumnText.videos,
          questions: certificationColumnText.questions,
          status: certificationColumnText.status,
        },
        (id) => t(`certifications.columns.${id as CertificationColumnId}`),
      ),
    [t],
  );

  const overrides = useMemo<TableFilterFieldOverrides>(
    () => ({
      exclude: ['validityDays', 'videos', 'questions'],
      types: { status: 'enum-one' },
      operators: { status: ['eq'] },
      options: {
        // HS-1511: named rather than literal. This override is what makes the field an `enum` —
        // without it `useTableFilterFields` infers `text`, and a link arriving with a lab id would
        // render as a free-text box holding a raw GUID. It has to move with the field id, so it
        // reads the same constant the param map and `certificationsFilteredByLab` do.
        [CERTIFICATIONS_LAB_FIELD]: toOptions(labNames),
        institute: toOptions(institutionNames),
        status: [
          { value: 'active', label: t('certifications.status.active') },
          { value: 'inactive', label: t('certifications.status.inactive') },
        ],
      },
      extra: [
        {
          id: CERTIFICATIONS_GROUP_FIELD,
          label: t('certifications.filters.group'),
          type: 'enum' as const,
          options: toOptions(groupNames),
        },
      ],
    }),
    [groupNames, institutionNames, labNames, t],
  );

  return <DataScreenFilter columns={columns} rows={data?.items ?? []} overrides={overrides} />;
}
